/**
 * The canonical error contract. Every non-2xx API response is the same
 * envelope, and clients branch on `code` — never on `message` text. The
 * offline sync engine's retry logic calls `isTransientErrorCode` to decide
 * whether a failed mutation should be retried or parked as permanently
 * failed; see coding-standards.md, Errors.
 */
import { z } from 'zod';
export const ErrorCode = z.enum([
    'VALIDATION_ERROR',
    'CONFLICT',
    'UNAUTHORIZED',
    'FORBIDDEN',
    'NOT_FOUND',
    'RATE_LIMITED',
    'INTERNAL_ERROR'
]);
/**
 * Codes worth retrying: the request was fine but the server (or the network
 * path to it) was momentarily unable to serve it. Every other code is
 * permanent — retrying without changing the request cannot succeed.
 */
const TRANSIENT_ERROR_CODES = new Set(['RATE_LIMITED', 'INTERNAL_ERROR']);
/**
 * The single source of truth for transient-vs-permanent classification. Put
 * new knowledge about a code's retryability here, not in the client that
 * calls it.
 */
export function isTransientErrorCode(code) {
    return TRANSIENT_ERROR_CODES.has(code);
}
/**
 * Best-effort HTTP status → canonical `ErrorCode`, shared by the server (a
 * non-`AppError` throw whose status must be mapped back onto our vocabulary)
 * and the client (a non-2xx response with no envelope — e.g. a Better Auth
 * error). Both sides must agree: the sync engine keys retry on the resulting
 * code, so a status classified two different ways would retry two different
 * ways. Anything unrecognised below 500 is a client mistake we have no better
 * word for; anything else is ours.
 */
const CODE_BY_STATUS = {
    400: 'VALIDATION_ERROR',
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    405: 'NOT_FOUND',
    406: 'VALIDATION_ERROR',
    409: 'CONFLICT',
    413: 'VALIDATION_ERROR',
    415: 'VALIDATION_ERROR',
    422: 'VALIDATION_ERROR',
    429: 'RATE_LIMITED'
};
export function errorCodeForStatus(status) {
    const mapped = CODE_BY_STATUS[status];
    if (mapped !== undefined)
        return mapped;
    return status >= 400 && status < 500 ? 'VALIDATION_ERROR' : 'INTERNAL_ERROR';
}
export const ErrorBody = z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
    correlation_id: z.string()
});
/** The full envelope shape every non-2xx API response uses. */
export const ErrorEnvelope = z.object({
    error: ErrorBody
});
//# sourceMappingURL=errors.js.map